import { useEffect, useState } from "react";
import type { DiscountCampaign, DiscountResponse } from "../types/discount";
import { isCampaignActive, navratriCampaign, scheduledCampaign } from "../utils/discountCampaign";

export function formatDiscountDateTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata" });
}
export function formatDiscountDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("en-IN", { dateStyle: "medium", timeZone: "Asia/Kolkata" });
}
export default function useActiveDiscount() {
  // Do not freeze a temporary offer into indefinitely cached, prerendered HTML.
  const [now, setNow] = useState<number | null>(() => typeof window === 'undefined' ? null : Date.now());
  const [remote, setRemote] = useState<DiscountCampaign | null>(null);
  const [campaigns, setCampaigns] = useState<DiscountCampaign[]>([]);
  const [loading, setLoading] = useState(() => typeof window === 'undefined' || !scheduledCampaign());
  const builtIn = now === null ? null : scheduledCampaign(now);
  const activeCampaign = builtIn || (now !== null && isCampaignActive(remote, now) ? remote : null);

  useEffect(() => {
    const update = () => setNow(Date.now());
    const boundaries = [navratriCampaign, remote].filter(Boolean).flatMap(c => [Date.parse(c!.startAt), Date.parse(c!.endAt) + 1]).filter(t => t > Date.now());
    const boundary = window.setTimeout(update, Math.min(30000, ...boundaries.map(t => t - Date.now())));
    window.addEventListener('focus', update);
    document.addEventListener('visibilitychange', update);
    return () => { window.clearTimeout(boundary); window.removeEventListener('focus', update); document.removeEventListener('visibilitychange', update); };
  }, [now, remote]);

  useEffect(() => {
    if (builtIn) { setLoading(false); return; }
    let cancelled = false;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8000);
    async function load() {
      try {
        const response = await fetch('/api/discounts', { cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error('Discount unavailable');
        const data = await response.json() as DiscountResponse;
        if (!cancelled) { setRemote(data.activeCampaign || null); setCampaigns(Array.isArray(data.campaigns) ? data.campaigns : []); }
      } catch { if (!cancelled) { setRemote(null); setCampaigns([]); } }
      finally { window.clearTimeout(timeout); if (!cancelled) setLoading(false); }
    }
    void load();
    return () => { cancelled = true; controller.abort(); window.clearTimeout(timeout); };
  }, [builtIn]);

  return { activeCampaign, campaigns: builtIn ? [builtIn, ...campaigns.filter(c => c.id !== builtIn.id)] : campaigns,
    discountActive: Boolean(activeCampaign), discountMultiplier: activeCampaign ? (100 - activeCampaign.percent) / 100 : 1,
    loading: builtIn ? false : loading };
}
